import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Plus, CheckCircle, Clock, AlertTriangle, X, ChevronDown,
  ListTodo, Users, Filter, AlertCircle
} from "lucide-react";
import { Card, GoldButton } from "../shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = ["operations", "originations", "underwriting", "processing", "lender_relations", "investor_relations"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const STATUSES = ["new", "in_progress", "waiting_on_borrower", "waiting_on_lender", "waiting_on_investor", "blocked", "completed", "cancelled"];

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-700", in_progress: "bg-amber-100 text-amber-700",
  waiting_on_borrower: "bg-purple-100 text-purple-700", waiting_on_lender: "bg-cyan-100 text-cyan-700",
  waiting_on_investor: "bg-indigo-100 text-indigo-700", blocked: "bg-red-100 text-red-700",
  completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-gray-100 text-gray-500",
};

const PRIORITY_STYLES = {
  low: "border-gray-200 text-gray-500", medium: "border-blue-200 text-blue-600",
  high: "border-amber-200 text-amber-600", critical: "border-red-200 text-red-600",
};

export default function TasksPanel() {
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterDept, setFilterDept] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterDept) params.append("department", filterDept);
    if (filterStatus) params.append("status", filterStatus);
    params.append("limit", "100");
    try {
      const res = await axios.get(`${API}/tasks?${params}`, { headers });
      setTasks(res.data.tasks || []);
      setCounts(res.data.counts || {});
    } catch {}
    finally { setLoading(false); }
  }, [filterDept, filterStatus]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status }, { headers });
      fetchTasks();
    } catch {}
  };

  const activeTasks = tasks.filter(t => !["completed", "cancelled"].includes(t.status));
  const completedTasks = tasks.filter(t => t.status === "completed");

  return (
    <div className="space-y-6" data-testid="tasks-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-blue-600" /> Task Management
          </h3>
          <p className="text-xs text-[#888] mt-1">
            {counts.new || 0} new &bull; {counts.in_progress || 0} in progress &bull; {counts.blocked || 0} blocked &bull; {counts.completed || 0} completed
          </p>
        </div>
        <GoldButton onClick={() => setShowCreate(true)} data-testid="create-task-btn">
          <Plus className="h-4 w-4 mr-1" /> New Task
        </GoldButton>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
          data-testid="filter-department"
          className="px-3 py-2 rounded-xl border border-cream-warm bg-cream text-sm focus:outline-none focus:border-emerald-400">
          <option value="">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          data-testid="filter-status"
          className="px-3 py-2 rounded-xl border border-cream-warm bg-cream text-sm focus:outline-none focus:border-emerald-400">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : (
        <>
          {/* Active Tasks */}
          <div className="space-y-2">
            {activeTasks.length === 0 ? (
              <Card hover={false}>
                <div className="text-center py-8 text-sm text-[#999]">
                  <CheckCircle className="h-8 w-8 mx-auto text-emerald-300 mb-2" />
                  No active tasks matching your filters
                </div>
              </Card>
            ) : activeTasks.map((task, i) => (
              <TaskRow key={task.task_id} task={task} onUpdate={updateTaskStatus} index={i} />
            ))}
          </div>

          {/* Completed Tasks (Collapsible) */}
          {completedTasks.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-[#888] hover:text-[#555] flex items-center gap-2 py-2">
                <ChevronDown className="h-4 w-4 group-open:rotate-180 transition-transform" />
                {completedTasks.length} completed task{completedTasks.length !== 1 ? "s" : ""}
              </summary>
              <div className="space-y-2 mt-2 opacity-60">
                {completedTasks.map(task => (
                  <TaskRow key={task.task_id} task={task} onUpdate={updateTaskStatus} />
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {/* Create Task Modal */}
      <AnimatePresence>
        {showCreate && <CreateTaskModal headers={headers} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchTasks(); }} />}
      </AnimatePresence>
    </div>
  );
}

function TaskRow({ task, onUpdate, index = 0 }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (index || 0) * 0.02 }}>
      <Card hover={false}>
        <div className="flex items-center gap-3" data-testid={`task-${task.task_id}`}>
          {/* Priority indicator */}
          <div className={`w-1 h-10 rounded-full border-2 ${PRIORITY_STYLES[task.priority] || ""}`} />

          {/* Task info */}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#0A0A0A] truncate">{task.title}</span>
              <span className={`px-1.5 py-0 rounded text-[9px] font-bold uppercase ${STATUS_STYLES[task.status] || "bg-gray-100 text-gray-600"}`}>
                {(task.status || "").replace(/_/g, " ")}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-[#888] mt-0.5">
              <span className="font-medium">{(task.department || "").replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
              {task.deal_id && <><span>&bull;</span><span>Deal: {task.deal_id.slice(0, 8)}...</span></>}
              {task.due_date && <><span>&bull;</span><span>Due: {new Date(task.due_date).toLocaleDateString()}</span></>}
              {task.priority === "critical" && <AlertTriangle className="h-3 w-3 text-red-500" />}
            </div>
          </div>

          {/* Quick status actions */}
          {task.status !== "completed" && task.status !== "cancelled" && (
            <div className="flex gap-1.5 shrink-0">
              {task.status === "new" && (
                <button onClick={() => onUpdate(task.task_id, "in_progress")} className="px-2 py-1 rounded-lg text-[10px] font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors" data-testid={`start-task-${task.task_id}`}>Start</button>
              )}
              <button onClick={() => onUpdate(task.task_id, "completed")} className="px-2 py-1 rounded-lg text-[10px] font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors" data-testid={`complete-task-${task.task_id}`}>
                <CheckCircle className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-3 pt-3 border-t border-cream-warm">
              {task.description && <p className="text-sm text-[#555] mb-2">{task.description}</p>}
              <div className="flex gap-2 flex-wrap text-[10px]">
                <span className={`px-2 py-0.5 rounded border ${PRIORITY_STYLES[task.priority] || ""}`}>{task.priority}</span>
                <span className="px-2 py-0.5 rounded bg-cream border border-cream-warm text-[#666]">Created: {new Date(task.created_at).toLocaleDateString()}</span>
                {task.completed_at && <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600">Done: {new Date(task.completed_at).toLocaleDateString()}</span>}
                {task.blocker_reason && <span className="px-2 py-0.5 rounded bg-red-50 text-red-600">Blocked: {task.blocker_reason}</span>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
}

function CreateTaskModal({ headers, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", department: "operations", priority: "medium", deal_id: "", due_date: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.deal_id) delete payload.deal_id;
      if (!payload.due_date) delete payload.due_date;
      await axios.post(`${API}/tasks`, payload, { headers });
      onCreated();
    } catch {}
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="create-task-modal">
        <div className="px-4 sm:px-6 py-4 border-b border-cream-warm flex items-center justify-between">
          <h3 className="text-base font-bold text-[#0A0A0A]">Create Task</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-cream"><X className="h-4 w-4 text-[#888]" /></button>
        </div>
        <div className="px-4 sm:px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#444] block mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none" data-testid="task-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#444] block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none resize-none h-16" data-testid="task-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Department</label>
              <select value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm bg-white" data-testid="task-dept-select">
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm bg-white" data-testid="task-priority-select">
                {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Deal ID (optional)</label>
              <input value={form.deal_id} onChange={e => setForm(p => ({ ...p, deal_id: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none" placeholder="Paste deal ID" data-testid="task-deal-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none" data-testid="task-due-input" />
            </div>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-4 border-t border-cream-warm flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-cream-warm text-sm text-[#666]">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !form.title.trim()} data-testid="task-submit-btn"
            className="px-5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50 transition-colors">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Task"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
